import { format } from "date-fns";

// Format time slot (start time to end time)
export const formatTimeSlot = (startsAt: Date | string, durationMinutes: number) => {
  try {
    const startDate = new Date(startsAt);
    const endDate = new Date(startDate.getTime() + durationMinutes * 60000);
    const formattedStart = format(startDate, "h:mm a");
    const formattedEnd = format(endDate, "h:mm a");
    return `${formattedStart} - ${formattedEnd}`;
  } catch (error) {
    return "Invalid time";
  }
};

// Get formatted duration string
export const formatDuration = (minutes: number) => {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
};

// Check if an appointment is in the past
export const isPastAppointment = (startsAt: Date | string) => {
  return new Date(startsAt) < new Date();
};

// Check if an appointment is currently in session
export const isInSessionAppointment = (startsAt: Date | string, durationMinutes: number) => {
  const now = new Date();
  const startDate = new Date(startsAt);
  const endDate = new Date(startDate.getTime() + durationMinutes * 60000);

  return startDate <= now && now <= endDate;
};

// Get appointment status
export type AppointmentStatus = "upcoming" | "in-session" | "completed";

export const getAppointmentStatus = (startsAt: Date | string, durationMinutes: number): AppointmentStatus => {
  if (isInSessionAppointment(startsAt, durationMinutes)) return "in-session";
  if (isPastAppointment(startsAt)) return "completed";
  return "upcoming";
};

// Group items by date
export const groupByDate = <T extends { startsAt: Date | string }>(items: T[]) => {
  const groups: Record<string, T[]> = {};

  items.forEach((item) => {
    const dateKey = new Date(item.startsAt).toDateString();
    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    groups[dateKey].push(item);
  });

  return Object.entries(groups).sort(([dateA], [dateB]) => {
    return new Date(dateA).getTime() - new Date(dateB).getTime();
  });
};

// Generate page numbers for pagination
export const generatePageNumbers = (currentPage: number, totalPages: number, maxPages: number = 5) => {
  const pageNumbers = [];
  let startPage = Math.max(1, currentPage - Math.floor(maxPages / 2));
  let endPage = Math.min(totalPages, startPage + maxPages - 1);

  if (endPage - startPage + 1 < maxPages) {
    startPage = Math.max(1, endPage - maxPages + 1);
  }

  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  return pageNumbers;
};
